import React, { useMemo } from "react";
import { Typography } from "antd";
import { computeTimelineLayout, estadoHitoMeta, dateOnly } from "./cronogramaUtils";
import dayjs from "dayjs";

const { Text } = Typography;
const ROW_H = 30;
const NAME_W = 190;
const HEAD_H = 28;

const Marker = ({ pct, color, label, dashed, top = 0 }) =>
  pct == null ? null : (
    <div
      style={{
        position: "absolute",
        left: `${pct}%`,
        top: 0,
        bottom: 0,
        borderLeft: `2px ${dashed ? "dashed" : "solid"} ${color}`,
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: top,
          // Near the right edge the label goes to the left of the line so it is not clipped.
          ...(pct > 85 ? { right: 5 } : { left: 3 }),
          fontSize: 10,
          lineHeight: "12px",
          color,
          whiteSpace: "nowrap",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
    </div>
  );

/**
 * Presentational Gantt-like timeline (CSS only): week columns, one bar per
 * hito colored by estado, a vertical marker for today and flags for the
 * committed / current delivery dates. Shared by the Cronograma tab and the
 * print page (`ocultarInterno` hides the internal reprogramming markers).
 */
const CronogramaTimeline = ({ hitos = [], fechaComprometida, fechaVigente: fechaVigenteProp, hoy, showToday = true, ocultarInterno = false }) => {
  // `ocultarInterno` (client PDF): the client only gets the initial schedule,
  // so the rescheduled date is ignored everywhere (range, markers, labels).
  const fechaVigente = ocultarInterno ? undefined : fechaVigenteProp;
  const layout = useMemo(
    () =>
      computeTimelineLayout(hitos, {
        hoy: showToday ? hoy || new Date() : "1900-01-01",
        fechaComprometida,
        fechaVigente,
      }),
    [hitos, fechaComprometida, fechaVigente, hoy, showToday]
  );

  if (!layout.weeks.length) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "#8c8c8c" }}>
        Aun no hay hitos para dibujar. Agrega hitos o usa «Sugerir desde la cotización».
      </div>
    );
  }

  const weekW = 100 / layout.weeks.length;
  const validHitos = hitos.filter((h) => h?.inicio && h?.fin);
  const hayReprogramada =
    fechaVigente && fechaComprometida && dateOnly(fechaVigente) !== dateOnly(fechaComprometida);

  return (
    <div style={{ display: "flex", border: "1px solid #f0f0f0", borderRadius: 6, overflow: "hidden", background: "#fff" }}>
      <div style={{ width: NAME_W, flex: "none", borderRight: "1px solid #f0f0f0" }}>
        <div style={{ height: HEAD_H, background: "#fafafa", borderBottom: "1px solid #f0f0f0", padding: "0 8px", lineHeight: `${HEAD_H}px`, fontSize: 12, color: "#595959" }}>
          Hito
        </div>
        {validHitos.map((h, i) => (
          <div
            key={h._id || h._key || i}
            title={h.nombre}
            style={{ height: ROW_H, lineHeight: `${ROW_H}px`, padding: "0 8px", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderBottom: "1px solid #f5f5f5" }}
          >
            {h.nombre || "Sin nombre"}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
        <div style={{ display: "flex", height: HEAD_H, background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
          {layout.weeks.map((w) => (
            <div key={w.start} style={{ width: `${weekW}%`, flex: "none", borderRight: "1px solid #f0f0f0", fontSize: 11, lineHeight: `${HEAD_H}px`, paddingLeft: 4, color: "#595959", overflow: "hidden", whiteSpace: "nowrap" }}>
              {w.label}
            </div>
          ))}
        </div>
        <div
          style={{
            position: "relative",
            backgroundImage: `linear-gradient(to right, #f0f0f0 1px, transparent 1px)`,
            backgroundSize: `${weekW}% 100%`,
          }}
        >
          {validHitos.map((h, i) => {
            const bar = layout.bars[i];
            const meta = estadoHitoMeta(h.estado);
            return (
              <div key={h._id || h._key || i} style={{ position: "relative", height: ROW_H, borderBottom: "1px solid #f5f5f5" }}>
                <div
                  title={`${h.nombre}: ${dayjs(dateOnly(h.inicio)).format("DD/MM")} - ${dayjs(dateOnly(h.fin)).format("DD/MM")} (${meta.label})`}
                  style={{
                    position: "absolute",
                    left: `${bar.leftPct}%`,
                    width: `${bar.widthPct}%`,
                    top: 6,
                    height: ROW_H - 12,
                    borderRadius: 4,
                    background: meta.color,
                    WebkitPrintColorAdjust: "exact",
                    printColorAdjust: "exact",
                  }}
                />
              </div>
            );
          })}
          <Marker pct={layout.todayPct} color="#1677ff" label="Hoy" dashed top={0} />
          <Marker pct={layout.comprometidaPct} color={hayReprogramada ? "#8c8c8c" : "#cf1322"} label={ocultarInterno ? "Fecha de entrega" : hayReprogramada ? "Fecha original" : "Entrega"} top={14} />
          {hayReprogramada && <Marker pct={layout.vigentePct} color="#cf1322" label="Entrega vigente" top={0} />}
        </div>
      </div>
    </div>
  );
};

export const CronogramaLeyenda = () => (
  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 6 }}>
    {["pendiente", "en_curso", "hecho"].map((e) => {
      const m = estadoHitoMeta(e);
      return (
        <Text key={e} style={{ fontSize: 12 }}>
          <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: m.color, marginRight: 4, WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} />
          {m.label}
        </Text>
      );
    })}
  </div>
);

export default CronogramaTimeline;
